/**
 * Result shapes of the public booking API, hand-written to match the
 * documents pinned in the backend manifest (see `operationIds.ts`).
 *
 * Every operation answers a union discriminated by `__typename`. The page
 * must treat a `__typename` it does not know as a generic error rather than
 * crash: the backend adds members over time, and a member added after a
 * deploy reaches this page before anyone updates these types.
 *
 * All instants are ISO 8601 UTC strings.
 */

/** Every `__typename` the eight operations can answer with. */
export enum BookingResultType {
  SCHEDULING_OFFER = 'SchedulingOffer',
  EXPIRED_LINK_STATE = 'ExpiredLinkState',
  NO_SLOTS_AVAILABLE_STATE = 'NoSlotsAvailableState',
  BOOKING_DETAILS = 'BookingDetails',
  NO_BOOKING_STATE = 'NoBookingState',
  LINK_REPLACED_STATE = 'LinkReplacedState',
  OFFER_CLOSED_STATE = 'OfferClosedState',
  HOLD_ACCEPTED = 'HoldAccepted',
  SEAT_UNAVAILABLE = 'SeatUnavailable',
  BOOKING_CONFIRMED = 'BookingConfirmed',
  HOLD_EXPIRED = 'HoldExpired',
  INVALID_TIMEZONE = 'InvalidTimezone',
  DECLINE_RECORDED = 'DeclineRecorded',
  CALENDAR_FILE_CONTENT = 'CalendarFileContent',
  BOOKING_CANCELLED_BY_CANDIDATE = 'BookingCancelledByCandidate',
  CANDIDATE_ACTION_NOT_ALLOWED = 'CandidateActionNotAllowed',
  NO_OTHER_TIMES_STATE = 'NoOtherTimesState',
  DECLINE_REASON_TOO_LONG = 'DeclineReasonTooLong',
}

/** The meeting service's `Conferencing` values, served as a plain string by the schema. */
export type Conferencing = 'teams' | 'none';

export interface ExpiredLinkState {
  __typename: BookingResultType.EXPIRED_LINK_STATE;
  expiredAt: string;
}

/**
 * The presented link was replaced by a newer one the candidate was sent (a
 * re-offer). Every operation can answer it, whatever it was asked.
 */
export interface LinkReplacedState {
  __typename: BookingResultType.LINK_REPLACED_STATE;
  replaced: boolean;
}

export interface SeatUnavailable {
  __typename: BookingResultType.SEAT_UNAVAILABLE;
  retryable: boolean;
}

// SchedulingOffer

export interface OfferedSeat {
  seatId: string;
  startsAt: string;
  endsAt: string;
  /** True for the one seat this candidate currently holds. */
  heldByYou: boolean;
}

export interface OfferedMaterial {
  label: string;
  url: string;
}

export interface SchedulingOfferPayload {
  __typename: BookingResultType.SCHEDULING_OFFER;
  companyName: string | null;
  roleName: string | null;
  durationMinutes: number;
  locationKind: string;
  /** `teams` when there is a video call. */
  conferencing: Conferencing;
  linkExpiresAt: string;
  seats: OfferedSeat[];
  materials: OfferedMaterial[];
  /**
   * Set when the hiring team is moving an interview the candidate already
   * booked (a reschedule offer): the time it stands at until they choose a
   * new one. `null` for a first invitation.
   */
  currentInterview: CurrentInterview | null;
}

/** The booked interview a reschedule offer would move. */
export interface CurrentInterview {
  startsAt: string;
  endsAt: string;
}

export interface NoSlotsAvailableState {
  __typename: BookingResultType.NO_SLOTS_AVAILABLE_STATE;
  available: boolean;
}

/**
 * The invitation is over and nothing is left to do here. The API deliberately
 * never says why: the hiring team cancelling the request and every other
 * closed state answer alike.
 */
export interface OfferClosedState {
  __typename: BookingResultType.OFFER_CLOSED_STATE;
  closed: boolean;
}

/**
 * `DeclineRecorded` is declared with the decline operation below; the offer
 * read answers it too, for a request the candidate has already declined.
 */
export type SchedulingOfferResult =
  | SchedulingOfferPayload
  | ExpiredLinkState
  | NoSlotsAvailableState
  | OfferClosedState
  | DeclineRecorded
  | LinkReplacedState;

// Booking

/**
 * Whether the page can offer the interview's join link. `ended` and `none`
 * never come with a link: the server withholds it once the interview is over.
 */
export enum JoinState {
  AVAILABLE = 'available',
  PENDING = 'pending',
  ENDED = 'ended',
  NONE = 'none',
}

/**
 * Why the candidate may not cancel or move their interview themselves, from
 * the booking read. The server's list is closed, but a value added after a
 * deploy reaches this page first, so callers must handle a string outside it.
 */
export enum CandidateActionBlockedReason {
  CUTOFF_PASSED = 'CUTOFF_PASSED',
  RESCHEDULE_LIMIT_REACHED = 'RESCHEDULE_LIMIT_REACHED',
  STARTED = 'STARTED',
}

/** A time the candidate could move their booked interview to. */
export interface RescheduleSeat {
  seatId: string;
  startsAt: string;
  endsAt: string;
}

export interface BookingDetails {
  __typename: BookingResultType.BOOKING_DETAILS;
  bookingId: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  candidateTimezone: string;
  status: string;
  /** A hint only: the cancel itself re-checks on the server. */
  canCancel: boolean;
  /** A hint only: the reschedule itself re-checks on the server. */
  canReschedule: boolean;
  /** Why neither is allowed, when neither is; otherwise null. */
  blockedReason: CandidateActionBlockedReason | string | null;
  /** The other times the candidate could move to. Empty when there are none. */
  otherTimes: RescheduleSeat[];
  companyName: string | null;
  roleName: string | null;
  durationMinutes: number;
  /** `teams` when there is a video call. */
  conferencing: Conferencing;
  joinState: JoinState;
  joinUrl: string | null;
  /** True when an AI note-taker is invited to the call. */
  spotJoins: boolean;
  materials: OfferedMaterial[];
  /** True when the calendar file is this interview's only calendar entry. */
  calendarFileOffered: boolean;
}

export interface NoBookingState {
  __typename: BookingResultType.NO_BOOKING_STATE;
  booked: boolean;
}

export type BookingResult = BookingDetails | NoBookingState | LinkReplacedState;

// HoldSlot

export interface HoldAccepted {
  __typename: BookingResultType.HOLD_ACCEPTED;
  seatId: string;
  holdExpiresAt: string;
}

export type HoldSlotResult =
  | HoldAccepted
  | SeatUnavailable
  | ExpiredLinkState
  | LinkReplacedState;

// ConfirmSlot

export interface BookingConfirmed {
  __typename: BookingResultType.BOOKING_CONFIRMED;
  bookingId: string;
}

export interface HoldExpired {
  __typename: BookingResultType.HOLD_EXPIRED;
  retryable: boolean;
}

export interface InvalidTimezone {
  __typename: BookingResultType.INVALID_TIMEZONE;
  retryable: boolean;
}

export type ConfirmSlotResult =
  | BookingConfirmed
  | HoldExpired
  | SeatUnavailable
  | InvalidTimezone
  | ExpiredLinkState
  | LinkReplacedState;

export interface ConfirmSlotVariables {
  seatId: string;
  /** IANA zone name such as `Europe/London`; a UTC offset is refused. */
  timezone: string;
  candidateTimezone: string;
}

// DeclineAll

/**
 * The longest decline note the server accepts: `DECLINE_REASON_MAX_LENGTH` in
 * the meeting service's `decline-all.service.ts`. Both count UTF-16 code
 * units, as `maxlength` does, so the box stops exactly where the server would
 * refuse.
 */
export const DECLINE_REASON_MAX_LENGTH = 1000;

export interface DeclineRecorded {
  __typename: BookingResultType.DECLINE_RECORDED;
  recorded: boolean;
}

/**
 * The note was over the server's limit, so nothing was recorded. The text box
 * caps input at the same limit, so this is reached only if the two disagree.
 */
export interface DeclineReasonTooLong {
  __typename: BookingResultType.DECLINE_REASON_TOO_LONG;
  maxLength: number;
}

export type DeclineAllResult =
  | DeclineRecorded
  | DeclineReasonTooLong
  | SeatUnavailable
  | ExpiredLinkState
  | LinkReplacedState;

// CancelBookingAsCandidate

/** The candidate's own cancel went through, or had already. */
export interface BookingCancelledByCandidate {
  __typename: BookingResultType.BOOKING_CANCELLED_BY_CANDIDATE;
  cancelled: boolean;
}

/**
 * Why a cancel or reschedule was refused. `BOOKING_CHANGED` and
 * `NO_LIVE_BOOKING` mean the page is stale; the rest are the same reasons the
 * booking read gives in `blockedReason`.
 */
export enum CandidateActionRefusalReason {
  BOOKING_CHANGED = 'BOOKING_CHANGED',
  CUTOFF_PASSED = 'CUTOFF_PASSED',
  RESCHEDULE_LIMIT_REACHED = 'RESCHEDULE_LIMIT_REACHED',
  NO_LIVE_BOOKING = 'NO_LIVE_BOOKING',
  STARTED = 'STARTED',
}

/** The candidate may not cancel or move this interview, for one reason from a closed list. */
export interface CandidateActionNotAllowed {
  __typename: BookingResultType.CANDIDATE_ACTION_NOT_ALLOWED;
  reason: CandidateActionRefusalReason | string;
  /** False, except for `BOOKING_CHANGED`, where a reload helps. */
  retryable: boolean;
}

export type CancelBookingAsCandidateResult =
  | BookingCancelledByCandidate
  | CandidateActionNotAllowed
  | LinkReplacedState;

// RescheduleBookingAsCandidate

/**
 * No other time is open to the candidate. Their interview stays as it is
 * and the hiring team has been told they asked.
 */
export interface NoOtherTimesState {
  __typename: BookingResultType.NO_OTHER_TIMES_STATE;
  contactRecruiter: boolean;
}

export type RescheduleBookingAsCandidateResult =
  | BookingConfirmed
  | NoOtherTimesState
  | CandidateActionNotAllowed
  | SeatUnavailable
  | InvalidTimezone
  | LinkReplacedState;

/**
 * Moving to a named seat needs both zones, as confirming a slot does. With
 * no seat the operation asks the hiring team for another time instead.
 */
export type RescheduleBookingVariables =
  | { bookingId: string }
  | ({ bookingId: string; seatId: string } & Omit<ConfirmSlotVariables, 'seatId'>);

// CalendarFile

export interface CalendarFileContent {
  __typename: BookingResultType.CALENDAR_FILE_CONTENT;
  filename: string;
  contentType: string;
  content: string;
}

export type CalendarFileResult =
  | CalendarFileContent
  | NoBookingState
  | LinkReplacedState;
